import React from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const AdminMain: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedKey = location.pathname;

  const [userName, setUserName] = React.useState("");
  const [affiliation, setAffiliation] = React.useState("");

  React.useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (location.pathname.startsWith("/register")) return;

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setUserName(data.name || "");
          setAffiliation(data.affiliation || "");
        } else {
          console.warn("Firestore에 사용자 정보가 없습니다.");
        }
      } else {
        if (!location.pathname.startsWith("/register")) {
          navigate("/login");
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      toast.success("로그아웃 되었습니다.");
      navigate("/login");
    } catch (error) {
      toast.error("로그아웃에 실패했습니다.");
    }
  };

  const navItems = [
    { path: "/admin/main/manage-data", label: "매출 데이터 관리" },
    { path: "/admin/main/warehouse-inventory", label: "창고 재고" },
    { path: "/admin/main/statistics-main", label: "통계" },
    { path: "/admin/main/manage-accounts", label: "계정관리" },
    { path: "/admin/main/ai-curation", label: "AI 큐레이션" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-4 bg-black px-6 py-4 text-white">
        <a
          href="https://buddie.kr/office-buddie/case-study/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center"
        >
          <img
            src="https://buddie.kr/images/img_footer_logo.png"
            alt="Logo"
            style={{ height: 40 }}
          />
        </a>

        <nav className="flex flex-1 items-center gap-2">
          {navItems.map((item) => {
            const active = selectedKey === item.path;
            return (
              <Button
                key={item.path}
                variant={active ? "secondary" : "ghost"}
                className={`h-10 rounded-md px-4 font-bold ${
                  active ? "bg-white text-black" : "text-white hover:bg-white/10"
                }`}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            );
          })}
        </nav>

        <div className="text-sm font-bold">
          {affiliation && userName && `${affiliation} ${userName} 님 환영합니다.`}
        </div>

        <Button
          variant="outline"
          className="ml-2 h-10 bg-white text-black hover:bg-white/90"
          onClick={handleLogout}
          title="로그아웃"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </header>

      <main className="p-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminMain;
